/**
 * v0.4.20.1 — Period quick-preset helpers (Q-calendar=a).
 *
 * Pure date arithmetic, no React/DOM. Tested via Node unit tests in
 * frontend/tests/periodPresets.test.mjs.
 *
 * Fiscal year convention: calendar year. (UK fiscal year starts
 * 6 April but our test suite + filing periods all align to Jan-Dec
 * for OSS and quarter boundaries; switch the FY anchor here if a
 * non-calendar FY is ever required.)
 */

const iso = (d) => {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const monthStart = (y, m /* 0-indexed */) => new Date(Date.UTC(y, m, 1));
const monthEnd = (y, m) => new Date(Date.UTC(y, m + 1, 0));

export function lastMonth(today = new Date()) {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const start = monthStart(y, m - 1);
  const end = monthEnd(y, m - 1);
  return { start: iso(start), end: iso(end) };
}

export function thisQuarter(today = new Date()) {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const qStart = Math.floor(m / 3) * 3;
  return { start: iso(monthStart(y, qStart)), end: iso(monthEnd(y, qStart + 2)) };
}

export function lastQuarter(today = new Date()) {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const qStart = Math.floor(m / 3) * 3 - 3;
  const sy = qStart < 0 ? y - 1 : y;
  const sm = ((qStart % 12) + 12) % 12;
  return { start: iso(monthStart(sy, sm)), end: iso(monthEnd(sy, sm + 2)) };
}

export function thisFiscalYear(today = new Date()) {
  const y = today.getUTCFullYear();
  return { start: `${y}-01-01`, end: `${y}-12-31` };
}

export function lastFiscalYear(today = new Date()) {
  const y = today.getUTCFullYear() - 1;
  return { start: `${y}-01-01`, end: `${y}-12-31` };
}

export function thisMonth(today = new Date()) {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  return { start: iso(monthStart(y, m)), end: iso(monthEnd(y, m)) };
}

// v0.6.10 — "Last quarter" alone never says WHICH quarter, so the
// operator had to click a chip and then read the two date fields to
// find out what it did. Each chip now carries its own resolved range.
// Deliberately terse: the year is omitted unless the range crosses
// one, because repeating the same year across six chips is noise.
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function presetRangeLabel(start, end) {
  if (!start || !end) return "";
  const [sy, sm, sd] = start.split("-").map(Number);
  const [ey, em, ed] = end.split("-").map(Number);
  if ([sy, sm, sd, ey, em, ed].some((n) => !Number.isFinite(n))) return "";
  // A whole calendar year reads as just the year.
  if (sy === ey && sm === 1 && sd === 1 && em === 12 && ed === 31) {
    return String(sy);
  }
  const s = `${sd} ${MONTH_ABBR[sm - 1]}`;
  const e = `${ed} ${MONTH_ABBR[em - 1]}`;
  return sy === ey ? `${s} – ${e}` : `${s} ${sy} – ${e} ${ey}`;
}

export const PRESETS = [
  { key: "this_month",    labelKey: "ui.calculate.preset_this_month",     fn: thisMonth },
  { key: "last_month",    labelKey: "ui.calculate.preset_last_month",     fn: lastMonth },
  { key: "this_quarter",  labelKey: "ui.calculate.preset_this_quarter",   fn: thisQuarter },
  { key: "last_quarter",  labelKey: "ui.calculate.preset_last_quarter",   fn: lastQuarter },
  { key: "this_fy",       labelKey: "ui.calculate.preset_this_fy",        fn: thisFiscalYear },
  { key: "last_fy",       labelKey: "ui.calculate.preset_last_fy",        fn: lastFiscalYear },
];
